import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import CommandPalette from '../CommandPalette';
import {
  LayoutDashboard,
  Users,
  Map,
  FileText,
  DollarSign,
  Calendar,
  BarChart3,
  Upload,
  User,
  Settings,
  Menu,
  X,
  LogOut,
  ExternalLink,
  Link2,
  Copy,
  Tag,
  Search,
  ClipboardList,
} from 'lucide-react';

const iconMap = {
  dashboard: LayoutDashboard,
  contacts: Users,
  counties: Map,
  declarations: FileText,
  donations: DollarSign,
  events: Calendar,
  reports: BarChart3,
  import: Upload,
  users: User,
  settings: Settings,
  integrations: Link2,
  duplicates: Copy,
  tags: Tag,
  audit: ClipboardList,
  journey: BarChart3, // Supporter Journey uses BarChart3 as closest lucide match
};

const navigation = [
  { name: 'Dashboard', href: '/', icon: 'dashboard' },
  { name: 'Contacts', href: '/contacts', icon: 'contacts' },
  { name: 'Supporter Journey', href: '/supporter-journey', icon: 'journey' },
  { name: 'Counties', href: '/counties', icon: 'counties' },
  { name: 'Declarations', href: '/declarations', icon: 'declarations' },
  { name: 'Donations', href: '/donations', icon: 'donations', roles: ['super_admin', 'admin', 'fundraising'] },
  { name: 'Donation Reports', href: '/donation-reports', icon: 'reports', roles: ['super_admin', 'admin', 'fundraising'] },
  { name: 'Events', href: '/events', icon: 'events' },
  { name: 'Reports', href: '/reports', icon: 'reports' },
];

const adminNavigation = [
  { name: 'Import', href: '/import', icon: 'import' },
  { name: 'Duplicates', href: '/duplicates', icon: 'duplicates' },
  { name: 'Tags', href: '/tags', icon: 'tags' },
  { name: 'Users', href: '/users', icon: 'users' },
  { name: 'Integrations', href: '/integrations', icon: 'integrations' },
  { name: 'Audit Log', href: '/audit-log', icon: 'audit' },
  { name: 'Settings', href: '/settings', icon: 'settings' },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch logo from backend with React Query caching
  const { data: logoUrl } = useQuery<string | null>({
    queryKey: ['org-logo'],
    queryFn: async () => {
      try {
        const response = await api.get('/settings/logo', { responseType: 'blob' });
        if (response.status === 200 && response.data) {
          const blob = response.data as Blob;
          return URL.createObjectURL(blob);
        }
        return null;
      } catch {
        return null;
      }
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    retry: false,
  });

  // Ctrl+K / Cmd+K to open command palette
  const handleCommandPaletteShortcut = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setCommandPaletteOpen((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleCommandPaletteShortcut);
    return () => {
      document.removeEventListener('keydown', handleCommandPaletteShortcut);
    };
  }, [handleCommandPaletteShortcut]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = hasRole('super_admin', 'admin');

  // Get page title from location
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    const segment = path.split('/')[1];
    return segment.charAt(0).toUpperCase() + segment.slice(1);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* A11Y-1: Skip to content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-tnm-navy focus:text-white focus:rounded-lg focus:text-sm focus:font-medium focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-tnm-navy to-tnm-navy-dark transform transition-transform duration-300 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-8 w-auto object-contain" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">T</span>
                </div>
              )}
              <span className="text-lg font-semibold text-white">TNM CRM</span>
            </div>
            <button
              className="lg:hidden p-1 rounded-md text-white/60 hover:text-white hover:bg-white/10"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Main navigation">
            {navigation
              .filter((item) => !item.roles || hasRole(...item.roles as string[]))
              .map((item) => {
                const Icon = iconMap[item.icon as keyof typeof iconMap];
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-150 ${
                        isActive
                          ? 'bg-white/15 text-white shadow-sm'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`
                    }
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                    {item.name}
                  </NavLink>
                );
              })}

            {isAdmin && (
              <>
                <div className="pt-4 mt-4 border-t border-white/10">
                  <p className="px-3 mb-2 text-xs font-semibold text-white/40 uppercase tracking-wider">
                    Administration
                  </p>
                </div>
                {adminNavigation.map((item) => {
                  const Icon = iconMap[item.icon as keyof typeof iconMap];
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-150 ${
                          isActive
                            ? 'bg-white/15 text-white shadow-sm'
                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`
                      }
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                      {item.name}
                    </NavLink>
                  );
                })}
              </>
            )}
          </nav>

          {/* User info */}
          <div className="p-3 border-t border-white/10">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="flex-shrink-0">
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white font-medium ring-2 ring-white/20">
                  {user?.first_name?.[0] || user?.email[0].toUpperCase()}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.first_name
                    ? `${user.first_name} ${user.last_name || ''}`
                    : user?.email}
                </p>
                <p className="text-xs text-white/50 truncate capitalize">
                  {user?.role.replace('_', ' ')}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" aria-hidden="true" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-slate-200/80">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 -ml-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open sidebar"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-lg font-semibold text-slate-900">{getPageTitle()}</h1>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCommandPaletteOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                aria-label="Open command palette"
              >
                <Search className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">Search...</span>
                <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 bg-white rounded border border-slate-200">
                  Ctrl K
                </kbd>
              </button>
              <a
                href="/the-count"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-tnm-navy hover:bg-tnm-navy/5 rounded-lg transition-colors"
              >
                View The Count
                <ExternalLink className="w-4 h-4" aria-hidden="true" />
              </a>
              {/* Notification bell removed — add back when notification system is implemented */}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="p-4 sm:p-6 lg:p-8">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
    </div>
  );
}
